/**
 * Ranking for Make Bundles component source locations:
 * stock before pick bins, then lowest height (3rd shelf-index field), then lowest aisle (1st).
 */

export interface BundleWorkPickLocation {
  readonly shelfIndex: string;
  readonly type: string | null;
}

function typeRank(type: string | null): number {
  const normalized = (type || '').toLowerCase().trim();
  if (normalized === 'stock') return 0;
  if (normalized === 'pick bin') return 1;
  return 2;
}

function isMissingShelfIndex(shelfIndex: string): boolean {
  const trimmed = shelfIndex.trim();
  return trimmed === '' || trimmed.toUpperCase() === 'N/A' || trimmed.toUpperCase() === 'NA';
}

function parseShelfParts(shelfIndex: string): { aisle: number; bay: string; height: number } {
  const [aisleRaw, bayRaw, heightRaw] = shelfIndex.split('-');
  const aisle = parseInt(aisleRaw ?? '', 10);
  const height = parseInt(heightRaw ?? '', 10);
  return {
    aisle: Number.isFinite(aisle) ? aisle : Number.POSITIVE_INFINITY,
    bay: bayRaw || 'Z',
    height: Number.isFinite(height) ? height : Number.POSITIVE_INFINITY,
  };
}

export function compareBundleWorkPickLocations(
  left: BundleWorkPickLocation,
  right: BundleWorkPickLocation,
): number {
  const leftMissing = isMissingShelfIndex(left.shelfIndex);
  const rightMissing = isMissingShelfIndex(right.shelfIndex);
  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;

  const typeCmp = typeRank(left.type) - typeRank(right.type);
  if (typeCmp !== 0) return typeCmp;

  const leftParts = parseShelfParts(left.shelfIndex);
  const rightParts = parseShelfParts(right.shelfIndex);
  if (leftParts.height !== rightParts.height) return leftParts.height - rightParts.height;
  if (leftParts.aisle !== rightParts.aisle) return leftParts.aisle - rightParts.aisle;
  return leftParts.bay.localeCompare(rightParts.bay);
}

export function pickPreferredBundleWorkLocation(
  locations: readonly BundleWorkPickLocation[],
): BundleWorkPickLocation | null {
  const ranked = locations.filter((location) => !isMissingShelfIndex(location.shelfIndex));
  if (ranked.length === 0) return null;
  return [...ranked].sort(compareBundleWorkPickLocations)[0] ?? null;
}
